// Lightweight analytics shim. Uses Lovable's built-in analytics if available
// (window.lovable.track), otherwise logs to console. Safe to import anywhere.

export type EventName =
  | 'signed_up'
  | 'signed_in'
  | 'child_created'
  | 'onboarding_completed'
  | 'milestone_completed'
  | 'milestone_uncompleted'
  | 'note_added'
  | 'journal_filtered'
  | 'journal_entry_clicked'
  | 'journal_viewed'
  | 'reddit_pulse_viewed'
  | 'reddit_pulse_preview_shown'
  | 'reddit_post_clicked'
  | 'waitlist_joined'
  | 'pro_upgrade_clicked'
  | 'beta_feedback_clicked'
  | 'beta_disclaimer_dismissed'
  | 'launch_list_opted_in'
  | 'launch_list_opted_out'
  | 'montessori_mode_enabled'
  | 'montessori_mode_disabled'
  | 'montessori_feedback_submitted'
  | 'bug_report_opened'
  | 'bug_report_submitted';

type Props = Record<string, unknown>;

declare global {
  interface Window {
    lovable?: { track?: (event: string, props?: Props) => void };
  }
}

export function track(event: EventName, props?: Props) {
  try {
    if (typeof window !== 'undefined' && window.lovable?.track) {
      window.lovable.track(event, props);
    } else if (import.meta.env.DEV) {
      console.info('[analytics]', event, props ?? {});
    }
  } catch (e) {
    // Never let analytics break the app
    console.warn('analytics failed', e);
  }
}

// Global error logger — captures uncaught errors and unhandled rejections so
// beta crashes show up in console + analytics instead of disappearing silently.
let errorLoggerInstalled = false;
export function installErrorLogger() {
  if (errorLoggerInstalled || typeof window === 'undefined') return;
  errorLoggerInstalled = true;

  window.addEventListener('error', (event) => {
    const message = event.message || 'unknown error';
    const source = `${event.filename ?? '?'}:${event.lineno ?? '?'}:${event.colno ?? '?'}`;
    console.error('[uncaught]', message, source, event.error);
    try {
      window.lovable?.track?.('client_error', {
        message: message.slice(0, 300),
        source,
        path: window.location.pathname,
      });
    } catch {
      /* ignore */
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason ?? 'unknown rejection');
    console.error('[unhandledrejection]', message, reason);
    try {
      window.lovable?.track?.('client_unhandled_rejection', {
        message: message.slice(0, 300),
        path: window.location.pathname,
      });
    } catch {
      /* ignore */
    }
  });
}
